import { Component, h, Listen, Event, EventEmitter, Prop, State } from '@stencil/core';
import { Customer } from '../../../globals/customer';
import { getCardType, ErrorHelper } from '../../../helpers';
import { PageKey } from '../../../globals/page-key';
import { updateChangeCard } from '../../../utils';
import { StatusCodeError } from '../../../utils/status-range-error';
import { AddCardTranslation } from '../../../globals/translations';

@Component({
  tag: 'add-card-info-state',
  styleUrl: './state-styles/add-card-info-state.scss',
  shadow: false,
})
export class AddCardInfoState {
  @State() addCardContinueDisabled = false;
  @State() prohibitedStatesCard: string[];

  @Prop({ mutable: true }) cardInfo: Customer['cardInfo'];
  @Prop({ mutable: true }) billingInfo: Customer['address']['billing'];
  @Prop({ mutable: true }) errors: ErrorHelper;
  @Prop() blockedStatesAddCard: string[];
  @Prop() translation: Partial<AddCardTranslation>;

  @Event() updateCustomer: EventEmitter<Partial<Customer>>;
  @Event() continueClick: EventEmitter;
  @Event() setView: EventEmitter<PageKey>;

  @Listen('changeCardInfo', { target: 'body' })
  async updateChangeCardInfo(event: CustomEvent) {
    this.errors.resetErrorFlags();
    const { cardNumber, cardExpDate, cardCvv, cardFirstName, cardLastName, cardBillingAddress } = event.detail;

    const [month, year] = cardExpDate.split('/');

    const cardExpDateFormatted = `${month}/20${year}`;

    const toChange = {
      cardInfo: {
        firstName: cardFirstName,
        lastName: cardLastName,
        cardNumber,
        cvv: cardCvv,
        expirationDate: cardExpDateFormatted,
        billingAddress: {
          line1: cardBillingAddress.billingStreetAddr1,
          line2: cardBillingAddress.billingStreetAddr2,
          city: cardBillingAddress.billingCity,
          state: cardBillingAddress.billingState,
          zip: cardBillingAddress.billingZipCode,
        },
      },
    };

    try {
      this.addCardContinueDisabled = true;
      const { status } = await updateChangeCard(toChange.cardInfo);

      if (status === 'CHANGE_CARD_SUCCESS') {
        const newBillingInfo = {
          line1: cardBillingAddress.billingStreetAddr1,
          line2: cardBillingAddress.billingStreetAddr2,
          city: cardBillingAddress.billingCity,
          state: cardBillingAddress.billingState,
          zip: cardBillingAddress.billingZipCode,
        };

        this.updateCustomer.emit({
          address: { billing: newBillingInfo } as Customer['address'],
          cardInfo: { ...toChange.cardInfo, expirationDate: cardExpDate, cardType: getCardType(cardNumber) },
        });

        this.billingInfo = newBillingInfo;

        this.errors.resetErrorFlags();
      }
    } catch (error) {
      if (error instanceof StatusCodeError) {
        const statusMsg = await error.getErrorStatusMessage();

        this.errors.setCCError(statusMsg);
      } else {
        this.errors.hasGenericError = true;
      }
    } finally {
      this.addCardContinueDisabled = false;
      this.setView.emit(PageKey.paymentSigning);
    }
  }

  render() {
    const addCardProps = { ...this.cardInfo, ...this.billingInfo, continueButtonDisabled: this.addCardContinueDisabled, showBack: false, translation: this.translation };

    return (
      <pc-add-card {...addCardProps} prohibitedStatesCard={this.blockedStatesAddCard}>
        {this.errors.hasGenericError && <pc-generic-error slot="error-card" />}
      </pc-add-card>
    );
  }
}
